import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ComicCard from '@/components/feature/ComicCard';
import useSeo from '@/hooks/useSeo';
import { getComicById, getRelated } from '@/mocks/comics';

export default function ComicDetail() {
  const { id = '' } = useParams();
  const comic = getComicById(id);

  const base = (window as unknown as { __BASE_PATH__?: string }).__BASE_PATH__ ?? '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  useSeo({
    title: comic ? `${comic.title} - 好看的韩国漫画评分高${comic.genre}推荐` : '漫画不存在',
    description: comic
      ? `${comic.title}是${comic.genre}类型的高分韩国漫画，作者${comic.author}，评分${comic.rating.toFixed(1)}、热度${comic.popularity}。${comic.summary}`
      : '未找到该漫画',
    keywords: comic
      ? `韩国漫画,${comic.title},${comic.genre},高分韩漫,韩漫推荐`
      : '韩国漫画',
    canonicalPath: comic ? `/comic/${comic.id}` : `/`,
    ogType: 'article',
    jsonLd: comic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: comic.title,
            description: comic.summary,
            author: { '@type': 'Person', name: comic.author },
            genre: comic.genre,
            datePublished: comic.year,
            image: comic.cover,
            inLanguage: 'zh-CN',
            url: `${origin}${base}/comic/${comic.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}${base}/` },
              { '@type': 'ListItem', position: 2, name: '韩国漫画', item: `${origin}${base}/ranking/popularity` },
              { '@type': 'ListItem', position: 3, name: comic.title, item: `${origin}${base}/comic/${comic.id}` },
            ],
          },
        ]
      : undefined,
  });

  if (!comic) {
    return (
      <Layout>
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center px-4 py-28 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这部漫画</h1>
          <p className="mt-2 text-sm text-foreground-500">该作品可能已下架或链接有误</p>
          <Link to="/ranking/popularity" className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
            返回人气榜
          </Link>
        </div>
      </Layout>
    );
  }

  const related = getRelated(comic, 4);

  return (
    <Layout>
      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to="/ranking/popularity" className="transition-colors hover:text-primary-700">韩国漫画</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">{comic.title}</span>
        </nav>

        <article className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-200">
              <img
                src={comic.cover}
                alt={`${comic.title} 好看的韩国漫画封面`}
                title={`${comic.title} 高分韩国漫画`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">{comic.genre}</span>
              {comic.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">{t}</span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">{comic.title}</h1>

            <p className="mt-3 text-sm text-foreground-600">
              作者：<span className="text-foreground-800">{comic.author}</span>
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                <p className="flex items-center justify-center gap-1 text-xl font-bold text-accent-600">
                  <i className="ri-star-fill text-sm" />
                  {comic.rating.toFixed(1)}
                </p>
                <p className="mt-1 text-xs text-foreground-500">读者评分</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                <p className="text-xl font-bold text-primary-700">{comic.popularity}</p>
                <p className="mt-1 text-xs text-foreground-500">当前热度</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                <p className="text-xl font-bold text-foreground-900">{comic.chapters}</p>
                <p className="mt-1 text-xs text-foreground-500">话数</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                <p className="text-xl font-bold text-foreground-900">{comic.status}</p>
                <p className="mt-1 text-xs text-foreground-500">连载状态</p>
              </div>
            </div>

            <h2 className="mt-8 font-heading text-lg font-bold text-foreground-950">作品简介</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comic.intro}</p>

            <h2 className="mt-8 font-heading text-lg font-bold text-foreground-950">作品亮点</h2>
            <ul className="mt-3 space-y-2">
              {comic.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2 text-sm text-foreground-700">
                  <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent-500 text-[10px] text-foreground-950">
                    <i className="ri-check-line" />
                  </span>
                  {h}
                </li>
              ))}
            </ul>
          </div>
        </article>

        <section className="mt-14">
          <div className="flex items-end justify-between">
            <div>
              <span className="flex items-center gap-2 text-sm font-semibold text-primary-600">
                <i className="ri-bookmark-3-fill text-base" />
                猜你喜欢
              </span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">相关高分韩漫推荐</h2>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}